import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { debounce } from 'lodash';
import { Mixpanel } from '../../../services/Mixpanel';
import {
  useDiffitBooks,
  cleanBookTitle,
  getListOfStartChapters,
  getListOfEndChapters,
} from '../../Homepage/Contexts/DiffitBooksContext';
import { BrowsingView } from './BrowsingView';
import { DetailView } from './DetailView';

/**
 * Modal dialog for browsing and selecting a book with a chapter range.
 * Displays a browsing view for searching/selecting a book, then a detail view for choosing chapters.
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {Function} props.onClose - Callback to close the modal.
 * @param {Function} props.onConfirm - Callback invoked with (selectedBook, startChapterIdx, endChapterIdx) on confirmation.
 */
export const BookSelectorModal = ({ isOpen, onClose, onConfirm }) => {
  const { diffitBooks = [] } = useDiffitBooks();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [startChapterIdx, setStartChapterIdx] = useState(-1);
  const [endChapterIdx, setEndChapterIdx] = useState(-1);
  const [view, setView] = useState('browsing');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedBook(null);
      setStartChapterIdx(-1);
      setEndChapterIdx(-1);
      setView('browsing');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const booksInSearch = useMemo(() => {
    if (!searchQuery) return diffitBooks;
    const lowerCaseQuery = searchQuery.toLowerCase().replace(/[^a-z0-9]/g, '');
    return diffitBooks.filter(
      (book) =>
        book.title
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '')
          .includes(lowerCaseQuery) ||
        book.author
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '')
          .includes(lowerCaseQuery)
    );
  }, [diffitBooks, searchQuery]);

  const debouncedMixpanelTrack = useCallback(
    debounce((event, props) => {
      Mixpanel.track(event, props);
    }, 1000),
    []
  );

  const startChapterOptions = useMemo(
    () => (selectedBook ? getListOfStartChapters(selectedBook.chapters, endChapterIdx) : []),
    [selectedBook, endChapterIdx]
  );

  const endChapterOptions = useMemo(
    () => (selectedBook ? getListOfEndChapters(selectedBook.chapters, startChapterIdx) : []),
    [selectedBook, startChapterIdx]
  );

  const selectBook = useCallback((book) => {
    setSelectedBook(book);
    setStartChapterIdx(-1);
    setEndChapterIdx(-1);
    setView('detail');
    Mixpanel.track('Unlimited Book Modal Selected Book', {
      book: cleanBookTitle(book.title),
    });
  }, []);

  const handleBack = () => {
    setView('browsing');
    setSelectedBook(null);
    setStartChapterIdx(-1);
    setEndChapterIdx(-1);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleConfirm = () => {
    if (!selectedBook || startChapterIdx === -1 || endChapterIdx === -1) return;
    onConfirm(selectedBook, startChapterIdx, endChapterIdx);
  };

  const canConfirm = selectedBook && startChapterIdx !== -1 && endChapterIdx !== -1;

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div className='absolute inset-0 bg-black/50' onClick={onClose} />
      <div
        className={`relative bg-white rounded-2xl shadow-xl max-w-3xl w-full mx-4 flex flex-col ${view === 'browsing' ? 'h-[85vh]' : 'max-h-[85vh]'}`}>
        <div className='flex items-center justify-between px-4 py-3 flex-shrink-0'>
          <div className='flex items-center gap-3'>
            {view === 'detail' && (
              <button
                onClick={handleBack}
                className='p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors'>
                <ArrowLeftIcon className='w-5 h-5' />
              </button>
            )}
            <h2 className='text-lg font-medium text-gray-900'>
              {view === 'browsing' ? 'Choose a Book' : 'Select Chapters'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className='p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors'>
            <XMarkIcon className='w-5 h-5' />
          </button>
        </div>

        {view === 'browsing' ? (
          <BrowsingView
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            booksInSearch={booksInSearch}
            diffitBooksLength={diffitBooks.length}
            inputRef={inputRef}
            debouncedMixpanelTrack={debouncedMixpanelTrack}
            onSelectBook={selectBook}
          />
        ) : (
          <DetailView
            selectedBook={selectedBook}
            startChapterIdx={startChapterIdx}
            setStartChapterIdx={setStartChapterIdx}
            endChapterIdx={endChapterIdx}
            setEndChapterIdx={setEndChapterIdx}
            startChapterOptions={startChapterOptions}
            endChapterOptions={endChapterOptions}
            canConfirm={canConfirm}
            onConfirm={handleConfirm}
            onCancel={onClose}
          />
        )}
      </div>
    </div>
  );
};
